import { html } from '../lib/html.js';
import { icon } from '../lib/icons.js';
import { getProgress, settings, actions, importDoc, resetAll, clearLocal } from '../lib/store.js';
import { syncState, refreshUser, signOut, deleteAccount, pushNow, rename, onSync } from '../lib/sync.js';
import { overallStats } from '../lib/content.js';
import { streaks } from '../../shared/progress.js';
import { ago, plural } from '../lib/format.js';
import { topbar, syncLabel } from '../components/chrome.js';
import { openSheet, confirmDialog } from '../components/sheet.js';
import { toast } from '../components/toast.js';
import { navigate } from '../lib/router.js';

const VERSION = typeof __BG_VERSION__ !== 'undefined' ? __BG_VERSION__ : 'dev';

function busy(btn, on, label) {
  btn.disabled = on;
  btn.classList.toggle('is-busy', on);
  btn.setAttribute('aria-busy', on ? 'true' : 'false');
  if (label) btn.setAttribute('aria-label', on ? label : '');
  if (!on) btn.removeAttribute('aria-label');
}

function accountSection() {
  const s = syncState();
  if (s.user) {
    const name = s.user.name || (getProgress().profile && getProgress().profile.name) || '';
    return html`<section class="section card card--pad account" aria-labelledby="acct-title">
      <div class="account__who">
        <span class="avatar avatar--lg avatar--user" aria-hidden="true">${(name || s.user.email || '?')[0].toUpperCase()}</span>
        <div class="grow">
          <h2 class="h3" id="acct-title">${name || 'Your account'}</h2>
          ${s.user.email ? html`<p class="account__email">${s.user.email}</p>` : ''}
          <p class="account__status account__status--${s.status}">${icon(s.status === 'offline' || s.status === 'error' ? 'cloud-off' : 'cloud', { size: 16 })}<span>${syncLabel(s)}${s.lastSyncAt && s.status === 'synced' ? ` · ${ago(s.lastSyncAt)}` : ''}</span></p>
        </div>
      </div>
      ${s.user.email
        ? html`<p class="small muted account__note">Your progress, notes and settings sync across every device where you sign in with this email.</p>`
        : html`<div class="callout account__note">${icon('mail', { size: 18 })}<p>Signing in now uses a code sent to your email. <a class="text-link" href="/signin?next=%2Fsettings">Add your email</a> to keep this account on your other devices.</p></div>`}
      <div class="account__actions">
        <button type="button" class="btn btn--secondary btn--small" data-sync>${icon('refresh', { size: 16 })} Sync now</button>
        <button type="button" class="btn btn--secondary btn--small" data-rename>${icon('type', { size: 16 })} Change name</button>
        <button type="button" class="btn btn--secondary btn--small" data-signout>${icon('logout', { size: 16 })} Sign out</button>
      </div>
    </section>`;
  }
  return html`<section class="section card card--pad account account--out" aria-labelledby="acct-title">
    <div class="account__hero">
      <span class="account__icon" aria-hidden="true">${icon('cloud', { size: 26 })}</span>
      <div>
        <h2 class="h3" id="acct-title">Keep your progress everywhere</h2>
        <p class="small muted">Right now your progress is saved on this device only. Sign in with your email to back it up and pick up where you left off on any phone or computer.</p>
      </div>
    </div>
    <div class="account__cta">
      <a class="btn btn--primary btn--block" href="/signin?next=%2Fsettings">${icon('mail', { size: 18 })} Sign in with email</a>
    </div>
    <p class="tiny muted account__fine">We email you a 6-digit code. New here? The same code creates your account.</p>
  </section>`;
}

function readingSection() {
  const s = settings();
  const seg = (key, options) => html`<div class="segmented" role="group">
    ${options.map(([value, label, ic]) => html`<button type="button" data-set="${key}" data-value="${value}" aria-pressed="${s[key] === value ? 'true' : 'false'}">${ic ? icon(ic, { size: 16 }) : ''}${label}</button>`)}
  </div>`;
  const toggle = (key, label, sub) => html`<div class="setting">
    <div class="setting__text"><p class="setting__label" id="lbl-${key}">${label}</p><p class="setting__sub">${sub}</p></div>
    <span class="switch"><input type="checkbox" role="switch" data-switch="${key}" aria-labelledby="lbl-${key}" ${s[key] ? 'checked' : ''}><span class="switch__track"></span><span class="switch__thumb"></span></span>
  </div>`;
  return html`<section class="section" aria-labelledby="read-title">
    <h2 class="h3" id="read-title">Reading</h2>
    <div class="card settings-card">
      ${toggle('sanskrit', 'Show the original Sanskrit', 'Open the Sanskrit text under every verse. It is always one tap away either way.')}
      ${toggle('iast', 'Include transliteration', 'With the Sanskrit, show how it sounds in Roman letters')}
      <div class="setting setting--stack">
        <p class="setting__label" id="lbl-size">Text size</p>
        ${seg('textSize', [['s', 'Small'], ['m', 'Default'], ['l', 'Large'], ['xl', 'Largest']])}
        <p class="setting__preview">You have a right to your actions alone, never to their fruits.</p>
      </div>
      <div class="setting setting--stack">
        <p class="setting__label">Appearance</p>
        ${seg('theme', [['system', 'Auto', 'system'], ['light', 'Day', 'sun'], ['dark', 'Night', 'moon']])}
      </div>
    </div>
  </section>`;
}

function dataSection() {
  const p = getProgress();
  const stats = overallStats(p);
  const st = streaks(p.days);
  const name = (p.profile && p.profile.name) || '';
  return html`<section class="section" aria-labelledby="you-title">
    <h2 class="h3" id="you-title">You</h2>
    <div class="card settings-card">
      ${syncState().user ? '' : html`<div class="setting setting--stack">
        <label class="setting__label" for="name">What should we call you?</label>
        <input class="input" id="name" type="text" maxlength="40" autocomplete="given-name" placeholder="Your first name (optional)" value="${name}">
      </div>`}
      <div class="setting setting--stack">
        <p class="setting__label">Your progress</p>
        <dl class="mini-stats">
          <div><dt>Lessons</dt><dd class="num">${stats.done}/${stats.total}</dd></div>
          <div><dt>Verses</dt><dd class="num">${stats.verses}/700</dd></div>
          <div><dt>Best streak</dt><dd class="num">${plural(st.best, 'day')}</dd></div>
        </dl>
      </div>
      <div class="setting setting--stack">
        <p class="setting__label">Backup</p>
        <p class="setting__sub">A file with all your progress, notes and settings. Importing merges it with what is here, so nothing is lost.</p>
        <div class="row" style="flex-wrap:wrap">
          <button type="button" class="btn btn--secondary btn--small" data-export>${icon('download', { size: 16 })} Export a backup</button>
          <label class="btn btn--secondary btn--small" for="import-file">${icon('upload', { size: 16 })} Import</label>
          <input class="sr-only" id="import-file" type="file" accept="application/json,.json" data-import>
        </div>
      </div>
      <div class="setting setting--stack">
        <p class="setting__label">Start over</p>
        <p class="setting__sub">Clears lessons, verses read, streaks, bookmarks and notes${syncState().user ? ' on every device' : ' on this device'}.</p>
        <div><button type="button" class="btn btn--danger btn--small" data-reset>${icon('reset', { size: 16 })} Reset progress</button></div>
      </div>
      ${syncState().user ? html`<div class="setting setting--stack">
        <p class="setting__label">Delete account</p>
        <p class="setting__sub">Permanently deletes your account and the cloud copy of your progress. This device keeps its copy.</p>
        <div><button type="button" class="btn btn--danger btn--small" data-delete>${icon('trash', { size: 16 })} Delete account</button></div>
      </div>` : ''}
    </div>
  </section>`;
}

export default async function settingsView() {
  return {
    title: 'Settings',
    html: html`<div class="settings">
      ${topbar({ back: '/', title: 'Settings' })}
      <header class="wrap page-head"><h1 class="h1">Settings</h1></header>
      <div class="wrap">
        <div data-account>${accountSection()}</div>
        ${readingSection()}
        ${dataSection()}
        <section class="section" aria-labelledby="about-title">
          <h2 class="h3" id="about-title">About</h2>
          <ul class="list">
            <li><a class="list__item" href="/guide"><span class="list__body"><span class="list__title">How it works</span><span class="list__sub">Chapters, lessons and verses, and how to find your way</span></span><span class="list__trail">${icon('chevron-right', { size: 18 })}</span></a></li>
            <li><a class="list__item" href="/about"><span class="list__body"><span class="list__title">About this app</span><span class="list__sub">Sources, translation, privacy and what's new</span></span><span class="list__trail">${icon('chevron-right', { size: 18 })}</span></a></li>
          </ul>
          <p class="tiny muted settings__version">Bhagavad Gita v${VERSION}</p>
        </section>
      </div>
    </div>`,
    mount(root) {
      const rerenderAccount = () => {
        const slot = root.querySelector('[data-account]');
        if (slot) slot.innerHTML = String(accountSection());
      };
      // The page was rendered before any async gap; show the current state.
      rerenderAccount();
      // Signing in or out changes more than the account card (reset wording,
      // delete account, the name field), so re-render the whole page then.
      const who = () => (syncState().user ? syncState().user.id : null);
      let lastUser = who();
      const off = onSync(() => {
        if (who() !== lastUser) {
          lastUser = who();
          navigate('/settings', { replace: true });
        } else {
          rerenderAccount();
        }
      });

      root.addEventListener('click', async (e) => {
        const t = e.target.closest('button, [data-set]');
        if (!t) return;
        if (t.dataset.set) {
          actions.setSettings({ [t.dataset.set]: t.dataset.value });
          for (const b of root.querySelectorAll(`[data-set="${t.dataset.set}"]`)) b.setAttribute('aria-pressed', b === t ? 'true' : 'false');
          return;
        }
        if (t.matches('[data-sync]')) {
          busy(t, true, 'Syncing');
          await refreshUser();
          await pushNow();
          busy(t, false);
          const s = syncState();
          toast(s.status === 'synced' ? 'Everything is synced' : syncLabel(s));
        } else if (t.matches('[data-rename]')) {
          const current = syncState().user?.name || '';
          const { el, close } = openSheet({
            title: 'Change name',
            body: html`<div class="field"><label class="field__label" for="rn">Your first name</label>
              <input class="input" id="rn" type="text" maxlength="40" value="${current}" autocomplete="given-name"></div>`,
            foot: html`<button type="button" class="btn btn--secondary" data-sheet-close>Cancel</button><button type="button" class="btn btn--primary" data-save>Save</button>`,
          });
          const input = el.querySelector('#rn');
          const save = async () => {
            const btn = el.querySelector('[data-save]');
            busy(btn, true, 'Saving');
            try {
              await rename(input.value.trim());
              actions.setName(input.value.trim());
              close();
              toast('Name updated');
            } catch (err) {
              busy(btn, false);
              toast(err.message);
            }
          };
          el.querySelector('[data-save]').addEventListener('click', save);
          input.addEventListener('keydown', (ev) => ev.key === 'Enter' && save());
          setTimeout(() => input.focus(), 60);
        } else if (t.matches('[data-signout]')) {
          const { el, close } = openSheet({
            title: 'Sign out?',
            body: html`<p class="muted">Your progress stays safe in your account. Choose whether this device keeps a copy.</p>`,
            foot: html`<button type="button" class="btn btn--secondary" data-keep>Sign out</button><button type="button" class="btn btn--danger" data-clear>Sign out and clear this device</button>`,
          });
          const act = async (clear) => {
            close();
            await signOut().catch(() => {});
            if (clear) clearLocal();
            toast(clear ? 'Signed out. This device was cleared.' : 'Signed out');
            navigate('/settings', { replace: true });
          };
          el.querySelector('[data-keep]').addEventListener('click', () => act(false));
          el.querySelector('[data-clear]').addEventListener('click', () => act(true));
        } else if (t.matches('[data-delete]')) {
          const ok = await confirmDialog({
            title: 'Delete your account?',
            message: 'This permanently deletes your account and the cloud copy of your progress and notes. This device keeps its own copy. This cannot be undone.',
            confirm: 'Delete account',
            danger: true,
          });
          if (!ok) return;
          try {
            await deleteAccount();
            toast('Account deleted');
            navigate('/settings', { replace: true });
          } catch (err) {
            toast(err.message);
          }
        } else if (t.matches('[data-export]')) {
          const blob = new Blob([JSON.stringify({ app: 'bg.vibecod3.app', exportedAt: new Date().toISOString(), progress: getProgress() }, null, 1)], { type: 'application/json' });
          const a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = `bhagavad-gita-backup-${new Date().toISOString().slice(0, 10)}.json`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(a.href), 1000);
          toast('Backup downloaded');
        } else if (t.matches('[data-reset]')) {
          const ok = await confirmDialog({
            title: 'Reset all progress?',
            message: syncState().user
              ? 'This clears your lessons, verses read, streaks, bookmarks and notes here and in your account. Export a backup first if you might want them back.'
              : 'This clears your lessons, verses read, streaks, bookmarks and notes on this device. Export a backup first if you might want them back.',
            confirm: 'Reset everything',
            danger: true,
          });
          if (!ok) return;
          await resetEverywhere();
          toast('Progress reset');
          navigate('/settings', { replace: true });
        }
      });

      root.addEventListener('change', async (e) => {
        const sw = e.target.closest('[data-switch]');
        if (sw) actions.setSettings({ [sw.dataset.switch]: sw.checked });
        const file = e.target.closest('[data-import]');
        if (file && file.files[0]) {
          try {
            const text = await file.files[0].text();
            const parsed = JSON.parse(text);
            importDoc(parsed.progress || parsed);
            toast('Backup imported and merged');
            navigate('/settings', { replace: true });
          } catch {
            toast('That file is not a Bhagavad Gita backup');
          }
          file.value = '';
        }
      });

      const nameInput = root.querySelector('#name');
      if (nameInput) nameInput.addEventListener('change', () => actions.setName(nameInput.value));
      return () => off();
    },
  };
}

async function resetEverywhere() {
  resetAll();
  if (syncState().user) await pushNow();
}
